import { deviceApi } from "#/api/deviceApi";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import Pagination from "#/components/buttons/Pagination";
import Popover from "#/components/overlays/Popover";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import { type Device, DeviceHardwareType } from "#/types/device";
import type { RfidTag } from "#/types/rfidTag";
import type { ScanHistory } from "#/types/scan-history";
import { sanitize } from "#/utils/StringUtils";
import { getItemStatusColor, getRandomColor } from "#/utils/ColorUtils";
import { exportToCSV } from "#/utils/CsvUtils";
import { logger } from "#/utils/logger";
import { Anchor, Badge, Button, CloseButton, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Props {
  device?: Device;
  /** Rendered at the start of the header row (e.g. the title). */
  headerStart?: ReactNode;
  /** When given, a close button ends the header row after the table controls. */
  onClose?: () => void;
}
const HistoricalDataTable = ({ device, headerStart, onClose }: Props) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const isRpi = device?.hardware_type === DeviceHardwareType.SGS_RPI;
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const headers = isRpi
    ? [
        { key: "createdAt", value: "Date" },
        { key: "item", value: "RFID Tag" },
        { key: "initial_weight", value: "Initial Weight" },
        { key: "gross_weight", value: "Gross Weight" },
        { key: "net_weight", value: "Net Weight" },
        { key: "tare_weight", value: "Tare Weight" },
        { key: "gas_added_weight", value: "Gas Added Weight" },
        { key: "refill_success", value: "Refill Success" },
      ]
    : [
        { key: "createdAt", value: "Date" },
        { key: "item", value: "RFID Tag" },
        { key: "status_label", value: "Status Label" },
        { key: "location", value: "Location" },
      ];
  const [sortBy, setSortBy] = useState<string>("id");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["historical-data", device, sortDirection, sortBy, page, limit],
    queryFn: async () => {
      if (!device) {
        return {
          items: [],
          total: 0,
          per_page: 0,
        };
      }
      const response = await deviceApi.getHistory(device?.id, {
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
      });
      if (!response.data) {
        throw new Error("Failed to fetch history");
      }
      return {
        items: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    enabled: device != undefined && device.id > 0,
    staleTime: 1000 * 60,
  });

  const handleExportCurrentPage = () => {
    const exportData = items.map((item: ScanHistory) => ({
      date: formatDateTimeForExport(item.createdAt),
      rfid_tags: item.item?.rfid_tags?.map((tag: RfidTag) => tag.rfid).join(", ") || "",
      ...(isRpi
        ? {
            initial_weight: item.initial_weight ?? "",
            gross_weight: item.gross_weight ?? "",
            net_weight: item.net_weight ?? "",
            tare_weight: item.tare_weight ?? "",
            gas_added_weight: item.gas_added_weight ?? "",
            refill_success: item.refill_success ? "Yes" : "No",
            refill_error_reason: item.refill_error_reason ?? "",
          }
        : {
            status_label: item.status_label ?? "",
            location: item.location ?? "",
            latitude: item.latitude ?? "",
            longitude: item.longitude ?? "",
          }),
    }));

    const csvHeaders = isRpi
      ? ["date", "rfid_tags", "initial_weight", "gross_weight", "net_weight", "tare_weight", "gas_added_weight", "refill_success", "refill_error_reason"]
      : ["date", "rfid_tags", "status_label", "location", "latitude", "longitude"];

    const headerLabels = isRpi
      ? {
          date: "Date",
          rfid_tags: "RFID Tags",
          initial_weight: "Initial Weight",
          gross_weight: "Gross Weight",
          net_weight: "Net Weight",
          tare_weight: "Tare Weight",
          gas_added_weight: "Gas Added Weight",
          refill_success: "Refill Success",
          refill_error_reason: "Refill Error Reason",
        }
      : {
          date: "Date",
          rfid_tags: "RFID Tags",
          status_label: "Status Label",
          location: "Location",
          latitude: "Latitude",
          longitude: "Longitude",
        };

    const note = t("common.utcNote");
    const filename = `device-${device?.id}-history-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, csvHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      if (!device) return;

      const response = await deviceApi.getHistory(device.id, {
        page: 1,
        limit: total,
        sortBy: sortBy,
        order: sortDirection,
      });

      const allItems = response.data.data;

      const exportData = allItems.map((item: ScanHistory) => ({
        date: formatDateTimeForExport(item.createdAt),
        rfid_tags: item.item?.rfid_tags?.map((tag: RfidTag) => tag.rfid).join(", ") || "",
        ...(isRpi
          ? {
              initial_weight: item.initial_weight ?? "",
              gross_weight: item.gross_weight ?? "",
              net_weight: item.net_weight ?? "",
              tare_weight: item.tare_weight ?? "",
              gas_added_weight: item.gas_added_weight ?? "",
              refill_success: item.refill_success ? "Yes" : "No",
              refill_error_reason: item.refill_error_reason ?? "",
            }
          : {
              status_label: item.status_label ?? "",
              location: item.location ?? "",
              latitude: item.latitude ?? "",
              longitude: item.longitude ?? "",
            }),
      }));

      const csvHeaders = isRpi
        ? ["date", "rfid_tags", "initial_weight", "gross_weight", "net_weight", "tare_weight", "gas_added_weight", "refill_success", "refill_error_reason"]
        : ["date", "rfid_tags", "status_label", "location", "latitude", "longitude"];

      const headerLabels = isRpi
        ? {
            date: "Date",
            rfid_tags: "RFID Tags",
            initial_weight: "Initial Weight",
            gross_weight: "Gross Weight",
            net_weight: "Net Weight",
            tare_weight: "Tare Weight",
            gas_added_weight: "Gas Added Weight",
            refill_success: "Refill Success",
            refill_error_reason: "Refill Error Reason",
          }
        : {
            date: "Date",
            rfid_tags: "RFID Tags",
            status_label: "Status Label",
            location: "Location",
            latitude: "Latitude",
            longitude: "Longitude",
          };

      const note = t("common.utcNote");
      const filename = `device-${device?.id}-history-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, csvHeaders, filename, headerLabels, note);
    } catch (error) {
      logger.error("Failed to export all history:", error);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex w-full items-center justify-between gap-4 border-b border-b-slate-300 pb-4">
        <div className="flex items-center gap-2">{headerStart}</div>
        <div className="flex items-center gap-3">
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
          <div className="h-8 w-px bg-gray-300" />
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
          >
            {t("common.exportCsv")}
          </Button>
          <Button
            leftSection={<IconRefresh size={18} />}
            variant="outline"
            color="green"
            onClick={() => refetch()}
            loading={isFetching}
          >
            {t("common.refresh")}
          </Button>
          {onClose && (
            <>
              <div className="h-8 w-px bg-gray-300" />
              <CloseButton onClick={onClose} />
            </>
          )}
        </div>
      </div>
      <div className="w-full flex-1 min-h-0 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isFetching}
          allowSorting={true}
          sortableColumns={[1]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 5,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td>
                  {item.createdAt ? (
                    <span>
                      {formatDate(item.createdAt, client)}
                      <br />
                      {formatTime(item.createdAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td>
                  <div className="flex flex-col gap-1 items-start">
                    {item.item?.rfid_tags && item.item.rfid_tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        <Badge
                          color={getRandomColor(item.item.rfid_tags[0].rfid)}
                          variant="light"
                          size="sm"
                        >
                          {item.item.rfid_tags[0].rfid}
                        </Badge>
                        {item.item.rfid_tags.length > 1 && (
                          <Popover
                            withArrow
                            withOverlay={true}
                            anchor={
                              <Badge
                                color="gray"
                                variant="white"
                                size="sm"
                                className="cursor-pointer"
                              >
                                {t("devices.historicalData.nMore", { count: item.item.rfid_tags.length - 1 })}
                              </Badge>
                            }
                          >
                            <div className="flex flex-col gap-2 ">
                              <span className="text-xs font-semibold text-slate-600">
                                {t("devices.historicalData.allTags")} ({item.item.rfid_tags.length})
                              </span>
                              <div className="flex flex-col gap-1 max-w-sm">
                                {item.item.rfid_tags.map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    color={getRandomColor(tag.rfid)}
                                    variant="dot"
                                    size="md"
                                    className="font-mono"
                                  >
                                    {tag.rfid}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </Popover>
                        )}
                      </div>
                    ) : (
                      <span>--</span>
                    )}
                    {item.item?.id && (
                      <Anchor
                        size="xs"
                        onClick={() => window.open(`/inventory/items/${item.item?.id}`, '_blank')}
                        className="cursor-pointer"
                        underline="always"
                      >
                        {t("common.view")}
                      </Anchor>
                    )}

                  </div>
                </Table.Td>
                {isRpi ? (
                  <>
                    <Table.Td>{item.initial_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.gross_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.net_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.tare_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.gas_added_weight ?? "--"}</Table.Td>
                    <Table.Td>
                      <div className="w-full flex flex-col">
                        <span>{item.refill_success ? "Yes" : "No"}</span>
                        {item.refill_error_reason && (
                          <span>{item.refill_error_reason}</span>
                        )}
                      </div>
                    </Table.Td>
                  </>
                ) : (
                  <>
                    <Table.Td>
                      {item.status_label ? (
                        <Badge
                          color={getItemStatusColor(item.status_label)}
                          variant="light"
                          size="md"
                        >
                          {sanitize(item.status_label)}
                        </Badge>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td>
                      <div className="flex flex-col gap-1 items-start">
                        <span>{item.location ?? "--"}</span>
                        {item.longitude && item.latitude && (
                          <Anchor
                            size="xs"
                            onClick={() =>
                              window.open(`https://www.google.com/maps?q=${item.latitude},${item.longitude}`, '_blank')
                            }
                            className="cursor-pointer"
                            underline="always"
                          >
                            {t("common.view")}
                          </Anchor>
                        )}
                      </div>
                    </Table.Td>
                  </>
                )}
              </Table.Tr>
            );
          })}
        />
      </div>
      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default HistoricalDataTable;
